import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, AlertTriangle, ChevronLeft } from 'lucide-react';
import { useContentStore } from '../../store/contentStore';
import type { ScenarioCard, AgeGroup, CategoryId, ActionStep } from '../../types';

type CardTab = 'scenario' | 'ask' | 'answer';

interface FormState {
  title: string;
  categoryId: CategoryId | '';
  ageGroup: AgeGroup;
  scenarioDescription: string;
  kidVersion: string;
  parentPrompt: string;
  revealAnswer: string;
  step1: string;
  step2: string;
  step3: string;
  whatChildCanSay: string;
  parentCoachingTip: string;
  practiceTogetherPrompt: string;
  memoryPhrase: string;
  tags: string;
}

const EMPTY_FORM: FormState = {
  title: '', categoryId: '', ageGroup: '7-9',
  scenarioDescription: '', kidVersion: '',
  parentPrompt: '',
  revealAnswer: '', step1: '', step2: '', step3: '',
  whatChildCanSay: '', parentCoachingTip: '', practiceTogetherPrompt: '', memoryPhrase: '',
  tags: '',
};

function formFromScenario(s: ScenarioCard): FormState {
  return {
    title: s.title, categoryId: s.categoryId, ageGroup: s.ageGroup,
    scenarioDescription: s.scenarioDescription, kidVersion: s.kidVersion ?? '',
    parentPrompt: s.parentPrompt,
    revealAnswer: s.revealAnswer,
    step1: s.actionSteps[0]?.text ?? '', step2: s.actionSteps[1]?.text ?? '', step3: s.actionSteps[2]?.text ?? '',
    whatChildCanSay: s.whatChildCanSay, parentCoachingTip: s.parentCoachingTip,
    practiceTogetherPrompt: s.practiceTogetherPrompt, memoryPhrase: s.memoryPhrase,
    tags: s.tags.join(', '),
  };
}

function buildActionSteps(f: FormState): ActionStep[] {
  return [f.step1, f.step2, f.step3]
    .map((text, i) => ({ step: i + 1, text: text.trim() }))
    .filter((s) => s.text.length > 0);
}

const AGE_OPTIONS: AgeGroup[] = ['4-6', '7-9', '10-12', 'all'];

const CARD_TABS: { id: CardTab; label: string; emoji: string }[] = [
  { id: 'scenario', label: 'Scenario', emoji: '📖' },
  { id: 'ask',      label: 'Ask',      emoji: '💬' },
  { id: 'answer',   label: 'Answer',   emoji: '✅' },
];

export default function ScenariosPanel() {
  const { categories, scenarios, addScenario, updateScenario, deleteScenario } = useContentStore();

  const [filterCat, setFilterCat] = useState<string>('all');
  const [filterAge, setFilterAge] = useState<string>('all');
  const [search, setSearch] = useState('');

  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<ScenarioCard | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [cardTab, setCardTab] = useState<CardTab>('scenario');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return scenarios.filter((s) => {
      if (filterCat !== 'all' && s.categoryId !== filterCat) return false;
      if (filterAge !== 'all' && s.ageGroup !== filterAge) return false;
      if (q && !s.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [scenarios, filterCat, filterAge, search]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setCardTab('scenario');
    setError('');
    setModal('add');
  };

  const openEdit = (s: ScenarioCard) => {
    setEditTarget(s);
    setForm(formFromScenario(s));
    setCardTab('scenario');
    setError('');
    setModal('edit');
  };

  const validate = (): string => {
    if (!form.title.trim()) return 'Title is required.';
    if (!form.categoryId) return 'Category is required.';
    if (!form.scenarioDescription.trim()) return 'Scenario description is required.';
    if (!form.parentPrompt.trim()) return 'Parent prompt (Ask) is required.';
    if (!form.revealAnswer.trim()) return 'Reveal answer is required.';
    if (!form.memoryPhrase.trim()) return 'Memory phrase is required.';
    return '';
  };

  const handleSave = () => {
    const err = validate();
    if (err) { setError(err); return; }

    const base = {
      categoryId: form.categoryId as CategoryId,
      title: form.title.trim(),
      ageGroup: form.ageGroup,
      scenarioDescription: form.scenarioDescription.trim(),
      kidVersion: form.kidVersion.trim(),
      parentPrompt: form.parentPrompt.trim(),
      revealAnswer: form.revealAnswer.trim(),
      actionSteps: buildActionSteps(form),
      whatChildCanSay: form.whatChildCanSay.trim(),
      parentCoachingTip: form.parentCoachingTip.trim(),
      practiceTogetherPrompt: form.practiceTogetherPrompt.trim(),
      memoryPhrase: form.memoryPhrase.trim(),
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };

    if (modal === 'add') {
      addScenario(base);
    } else if (modal === 'edit' && editTarget) {
      updateScenario(editTarget.id, base);
    }
    setModal(null);
  };

  const field = (
    label: string,
    key: keyof FormState,
    opts?: { placeholder?: string; rows?: number; required?: boolean }
  ) => (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
        {label}{opts?.required && ' *'}
      </label>
      {(opts?.rows ?? 1) > 1 ? (
        <textarea
          value={form[key] as string}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={opts?.placeholder}
          rows={opts?.rows ?? 3}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 resize-none"
        />
      ) : (
        <input
          type="text"
          value={form[key] as string}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={opts?.placeholder}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
        />
      )}
    </div>
  );

  const catById = (id: string) => categories.find((c) => c.id === id);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500 shrink-0">{filtered.length} / {scenarios.length}</p>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={openAdd}
          className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-sm hover:bg-primary-700 transition-colors shrink-0"
        >
          <Plus size={15} /> Add Scenario
        </motion.button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-600 bg-white outline-none shrink-0"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
          ))}
        </select>
        <select
          value={filterAge}
          onChange={(e) => setFilterAge(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-600 bg-white outline-none shrink-0"
        >
          <option value="all">All ages</option>
          {AGE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-600 bg-white outline-none min-w-0"
        />
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">No scenarios match the filters.</div>
        )}
        {filtered.map((s) => {
          const cat = catById(s.categoryId);
          return (
            <div key={s.id} className="bg-white rounded-2xl p-3.5 shadow-card flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${cat?.color ?? 'from-gray-300 to-gray-400'} flex items-center justify-center text-base flex-shrink-0`}>
                {cat?.icon ?? '❓'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-gray-800 text-sm leading-tight truncate">{s.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-gray-400 text-xs">{cat?.name ?? s.categoryId}</span>
                  <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-1.5 py-0.5 rounded-full">{s.ageGroup}</span>
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-primary-100 text-gray-500 hover:text-primary-600 transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => setDeleteConfirm(s.id)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-50" onClick={() => setDeleteConfirm(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="fixed inset-x-4 top-1/3 z-50 bg-white rounded-3xl p-6 shadow-2xl max-w-sm mx-auto">
              <AlertTriangle size={32} className="text-red-400 mx-auto mb-3" />
              <h3 className="font-display font-bold text-gray-800 text-center mb-2">Delete Scenario?</h3>
              <p className="text-gray-500 text-sm text-center mb-5">This cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-2xl font-bold text-sm">Cancel</button>
                <button onClick={() => { deleteScenario(deleteConfirm); setDeleteConfirm(null); }} className="flex-1 bg-red-500 text-white py-3 rounded-2xl font-bold text-sm hover:bg-red-600">Delete</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {modal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" onClick={() => setModal(null)} />
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[92vh]"
            >
              {/* Header */}
              <div className="px-4 pt-4 pb-3 flex-shrink-0">
                <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3" />
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-display text-lg font-extrabold text-gray-800">
                    {modal === 'add' ? 'Add Scenario' : 'Edit Scenario'}
                  </h2>
                  <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>
              </div>

              {/* Card tabs */}
              <div className="px-4 pb-3 flex-shrink-0">
                <div className="flex bg-gray-100 rounded-2xl p-1">
                  {CARD_TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setCardTab(t.id)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        cardTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400'
                      }`}
                    >
                      <span>{t.emoji}</span> {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scrollable form area */}
              <div className="overflow-y-auto flex-1 px-4 pb-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={cardTab}
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-4"
                  >
                    {/* ── SCENARIO CARD ─────────────────────────────────── */}
                    {cardTab === 'scenario' && (
                      <>
                        {field('Title', 'title', { placeholder: 'e.g. What if a stranger asks for help?', required: true })}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Category *</label>
                            <select
                              value={form.categoryId}
                              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value as CategoryId | '' }))}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 bg-white outline-none focus:ring-2 focus:ring-primary-200"
                            >
                              <option value="">Select…</option>
                              {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Age Group</label>
                            <select
                              value={form.ageGroup}
                              onChange={(e) => setForm((f) => ({ ...f, ageGroup: e.target.value as AgeGroup }))}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 bg-white outline-none focus:ring-2 focus:ring-primary-200"
                            >
                              {AGE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                          </div>
                        </div>

                        {field('Scenario Description', 'scenarioDescription', { placeholder: 'Describe the situation the child faces…', rows: 3, required: true })}
                        {field('Kid-Friendly Version', 'kidVersion', { placeholder: 'Simpler version shown in Kid Mode…', rows: 2 })}
                        {field('Tags (comma separated)', 'tags', { placeholder: 'strangers, safety, no' })}
                      </>
                    )}

                    {/* ── ASK CARD ──────────────────────────────────────── */}
                    {cardTab === 'ask' && (
                      <>
                        <div className="bg-primary-50 rounded-2xl p-3 text-xs text-primary-700 font-medium mb-1">
                          💬 The parent prompt — what you ask your child before revealing the answer.
                        </div>
                        {field('Parent Prompt', 'parentPrompt', { placeholder: 'Ask your child: "What would you do if…?"', rows: 3, required: true })}
                      </>
                    )}

                    {/* ── ANSWER CARD ───────────────────────────────────── */}
                    {cardTab === 'answer' && (
                      <>
                        {field('Reveal Answer', 'revealAnswer', { placeholder: 'The core answer revealed after the prompt…', rows: 3, required: true })}

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Action Steps</label>
                          <div className="space-y-2">
                            {(['step1', 'step2', 'step3'] as const).map((key, i) => (
                              <div key={key} className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                                <input
                                  type="text"
                                  value={form[key]}
                                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                                  placeholder={`Step ${i + 1}`}
                                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {field('What Child Can Say', 'whatChildCanSay', { placeholder: '"No! I need to go inside!" then run.' })}
                        {field('Parent Coaching Tip', 'parentCoachingTip', { placeholder: 'Coaching advice for parents…', rows: 2 })}
                        {field('Practice Together Prompt', 'practiceTogetherPrompt', { placeholder: 'How to role-play this together…', rows: 2 })}
                        {field('Memory Phrase', 'memoryPhrase', { placeholder: 'Stay calm, stay safe!', required: true })}
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3 mt-4">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                {/* Navigation between tabs + save */}
                <div className="flex gap-3 mt-5">
                  {cardTab !== 'scenario' && (
                    <button
                      onClick={() => setCardTab(cardTab === 'answer' ? 'ask' : 'scenario')}
                      className="flex items-center gap-1 border-2 border-gray-200 text-gray-600 px-4 py-3 rounded-2xl font-bold text-sm"
                    >
                      <ChevronLeft size={14} /> Back
                    </button>
                  )}
                  {cardTab !== 'answer' ? (
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setCardTab(cardTab === 'scenario' ? 'ask' : 'answer')}
                      className="flex-1 bg-primary-600 text-white py-3 rounded-2xl font-bold text-sm shadow-md hover:bg-primary-700 transition-colors"
                    >
                      Next →
                    </motion.button>
                  ) : (
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={handleSave}
                      className="flex-1 bg-green-500 text-white py-3 rounded-2xl font-bold text-sm shadow-md hover:bg-green-600 transition-colors"
                    >
                      {modal === 'add' ? 'Add Scenario' : 'Save Changes'}
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
